import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronUp, Lightbulb, ShieldCheck, MapPin } from 'lucide-react';
import { JUDICIAL_PROCESS, SCOTLAND_JUDICIAL_PROCESS } from '../data/content';
import PrintButton from '../components/PrintButton';
import SEO from '../components/SEO';
import HelplinesStrip from '../components/HelplinesStrip';
import NationPicker from '../components/NationPicker';
import { IfEnglandWales, IfScotland, useNation } from '../i18n/NationContext';

function Stage({ stage, idx, isLast, openIds, toggle }) {
  const open = openIds.has(stage.id);
  return (
    <div className="relative pl-12" data-testid={`stage-${stage.id}`}>
      {/* Vertical line */}
      {!isLast && (
        <span
          aria-hidden
          className="absolute"
          style={{
            left: 18,
            top: 36,
            bottom: -10,
            width: 2,
            background: 'linear-gradient(180deg, var(--tf-purple-light) 0%, transparent 100%)',
          }}
        />
      )}
      {/* Dot */}
      <div
        className="absolute flex items-center justify-center"
        style={{
          left: 0,
          top: 0,
          width: 38,
          height: 38,
          borderRadius: 19,
          background: '#fff',
          border: `2px solid var(--tf-purple)`,
          color: 'var(--tf-purple)',
          fontFamily: 'Fraunces, serif',
          fontWeight: 600,
          fontSize: 15,
        }}
      >
        {idx + 1}
      </div>

      <button
        type="button"
        onClick={() => toggle(stage.id)}
        data-testid={`stage-toggle-${stage.id}`}
        className="w-full text-left tf-card p-5 transition-shadow"
        style={{ marginBottom: 16, cursor: 'pointer' }}
      >
        <div className="flex items-start gap-3">
          <div className="flex-1 min-w-0">
            <h3
              className="font-heading"
              style={{ fontSize: 19, color: 'var(--tf-text)', letterSpacing: '-0.01em' }}
            >
              {stage.title}
            </h3>
            <p
              className="font-body mt-1"
              style={{ fontSize: 12.5, color: 'var(--tf-purple)', fontWeight: 700, letterSpacing: '0.02em' }}
            >
              {stage.subtitle}
            </p>
          </div>
          {open ? (
            <ChevronUp size={20} color="var(--tf-text-muted)" />
          ) : (
            <ChevronDown size={20} color="var(--tf-text-muted)" />
          )}
        </div>

        <div
          className="mt-4 pt-4 print-show"
          data-print-expand="true"
          style={{
            borderTop: '1px solid var(--tf-card-border)',
            display: open ? 'block' : 'none',
          }}
        >
            <div className="tf-prose">
              {stage.body.split('\n\n').map((para, i) => (
                <p key={i} style={{ fontSize: 14, lineHeight: 1.7 }}>
                  {para}
                </p>
              ))}
            </div>
            {stage.tips && stage.tips.length > 0 && (
              <div
                className="mt-4 p-4 rounded-xl"
                style={{ background: 'var(--tf-pink-soft)' }}
              >
                <div className="flex items-center gap-2 mb-2">
                  <Lightbulb size={15} color="var(--tf-purple-dark)" />
                  <p
                    className="font-body font-bold"
                    style={{ fontSize: 11.5, color: 'var(--tf-purple-dark)', letterSpacing: '0.1em' }}
                  >
                    GOOD TO KNOW
                  </p>
                </div>
                <ul className="flex flex-col gap-2">
                  {stage.tips.map((t, i) => (
                    <li key={i} className="flex gap-2">
                      <span
                        style={{
                          width: 5,
                          height: 5,
                          borderRadius: 2.5,
                          background: 'var(--tf-purple)',
                          marginTop: 9,
                          flexShrink: 0,
                        }}
                      />
                      <p
                        className="font-body"
                        style={{ fontSize: 13, lineHeight: 1.55, color: 'var(--tf-text)' }}
                      >
                        {t}
                      </p>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
      </button>
    </div>
  );
}

export default function Process() {
  const { nation } = useNation();
  const stages = nation === 'scotland' ? SCOTLAND_JUDICIAL_PROCESS : JUDICIAL_PROCESS;
  const [openIds, setOpenIds] = useState(new Set([stages[0].id]));

  const toggle = (id) => {
    setOpenIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const expandAll = () => setOpenIds(new Set(stages.map((s) => s.id)));
  const collapseAll = () => setOpenIds(new Set());

  return (
    <div className="max-w-3xl mx-auto px-5 sm:px-8 py-10" data-testid="process-screen">
      <SEO
        title="The Judicial Process — Sexual Violence (UK)"
        description="Step-by-step UK guide for survivors of rape and sexual violence — reporting, police investigation, CPS, court, verdict, sentencing and appeals."
        path="/app/process"
      />
      <p className="eyebrow">THE JUDICIAL PROCESS — GENTLY EXPLAINED</p>
      <h1
        className="font-heading mt-3"
        style={{ fontSize: 'clamp(28px, 4vw, 36px)', lineHeight: 1.15, color: 'var(--tf-text)', letterSpacing: '-0.01em' }}
      >
        Every stage, explained gently.
      </h1>
      <p
        className="font-body mt-4 max-w-xl"
        style={{ fontSize: 14.5, color: 'var(--tf-text-secondary)', lineHeight: 1.65 }}
      >
        A trauma-informed walkthrough of how a rape or sexual offence case moves through the
        criminal justice system. Choose your nation below so we can show you the right
        pathway. Tap any stage to read more — go at your own pace.
      </p>

      <NationPicker testid="process-nation-picker" />

      <HelplinesStrip variant="sv" />

      {/* Reassuring trauma-informed message */}
      <section
        className="mt-6 tf-card p-5 sm:p-6"
        data-testid="sv-reassurance"
        style={{
          background: 'linear-gradient(180deg, #FFFFFF 0%, #F4EFF8 100%)',
          borderColor: 'var(--tf-purple-light)',
        }}
      >
        <div className="flex items-start gap-4">
          <div
            className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0"
            style={{
              background:
                'linear-gradient(135deg, var(--tf-purple) 0%, var(--tf-pink) 100%)',
              color: '#fff',
              boxShadow: '0 8px 22px rgba(139,123,177,0.28)',
            }}
          >
            <ShieldCheck size={20} />
          </div>
          <div className="flex-1 min-w-0">
            <p
              className="font-body font-bold"
              style={{
                fontSize: 10.5,
                letterSpacing: '0.22em',
                color: 'var(--tf-purple)',
              }}
            >
              BEFORE YOU READ ON
            </p>
            <h2
              className="font-heading mt-2"
              style={{
                fontSize: 'clamp(20px, 2.4vw, 23px)',
                color: 'var(--tf-text)',
                letterSpacing: '-0.01em',
                lineHeight: 1.25,
              }}
            >
              You are believed. It was not your fault.
            </h2>
            <p
              className="font-body mt-3"
              style={{
                fontSize: 13.5,
                color: 'var(--tf-text-secondary)',
                lineHeight: 1.7,
              }}
            >
              However you froze, fawned, complied or went quiet — that was your nervous
              system keeping you alive, not consent. What happened is not because of what you
              wore, what you said, what you didn&apos;t say, or who you trusted.
            </p>
            <p
              className="font-body mt-2"
              style={{
                fontSize: 13.5,
                color: 'var(--tf-text-secondary)',
                lineHeight: 1.7,
              }}
            >
              You do not have to report to deserve help. You can read a little, leave, and
              come back. <strong style={{ color: 'var(--tf-purple-dark)' }}>You set the pace.</strong>
            </p>
            <p
              className="font-body mt-4 inline-flex items-center gap-2"
              style={{
                fontSize: 11.5,
                fontWeight: 700,
                letterSpacing: '0.16em',
                color: 'var(--tf-purple-dark)',
              }}
            >
              YOU ARE BELIEVED · YOU ARE NOT ALONE
            </p>
          </div>
        </div>
      </section>

      <IfScotland>
      <section
        className="mt-8 tf-card p-5 sm:p-6"
        data-testid="process-scotland-scope"
        style={{
          background: 'linear-gradient(180deg, #FFFFFF 0%, #F4EFF8 100%)',
          borderColor: 'var(--tf-purple-light)',
        }}
      >
        <div className="flex items-start gap-4">
          <div
            className="w-11 h-11 rounded-2xl flex items-center justify-center flex-shrink-0"
            style={{
              background:
                'linear-gradient(135deg, var(--tf-purple) 0%, var(--tf-pink) 100%)',
              color: '#fff',
            }}
            aria-hidden="true"
          >
            <MapPin size={20} />
          </div>
          <div className="min-w-0 flex-1">
            <p
              className="font-body font-bold"
              style={{ fontSize: 10.5, letterSpacing: '0.22em', color: 'var(--tf-purple-dark)' }}
            >
              A COMPLETELY SEPARATE LEGAL SYSTEM
            </p>
            <h2
              className="font-heading mt-1"
              style={{
                fontSize: 'clamp(20px, 2.4vw, 23px)',
                color: 'var(--tf-text)',
                letterSpacing: '-0.01em',
                lineHeight: 1.25,
              }}
            >
              You are reading the Scottish judicial process.
            </h2>
            <p
              className="font-body mt-3"
              style={{ fontSize: 13.5, color: 'var(--tf-text-secondary)', lineHeight: 1.7 }}
            >
              Scotland is not part of the England &amp; Wales legal system — it has its own
              police (<strong>Police Scotland</strong>), its own prosecutor
              (<strong>COPFS</strong>, not the CPS), its own courts (<strong>Sheriff Court /
              High Court of Justiciary</strong>), its own specialist advocacy service
              (<strong>Rape Crisis Scotland&apos;s National Advocacy Project</strong>) — and
              since 1 January 2026 only two verdicts (<em>Guilty</em> or <em>Not Guilty</em>).
              Every stage below is the Scottish process.
            </p>
          </div>
        </div>
      </section>
      </IfScotland>

      <div className="flex gap-2 mt-6 mb-8 flex-wrap">
        <button
          type="button"
          onClick={expandAll}
          data-testid="expand-all"
          className="tf-no-print px-3 py-1.5 rounded-full text-[12px] font-bold"
          style={{ background: '#fff', color: 'var(--tf-purple)', border: '1px solid var(--tf-purple-light)' }}
        >
          Expand all
        </button>
        <button
          type="button"
          onClick={collapseAll}
          data-testid="collapse-all"
          className="tf-no-print px-3 py-1.5 rounded-full text-[12px] font-bold"
          style={{ background: 'var(--tf-bg-secondary)', color: 'var(--tf-text-secondary)' }}
        >
          Collapse all
        </button>
        <PrintButton label="Print / Save this page" />
      </div>

      <div data-testid={nation === 'scotland' ? 'process-stages-scotland' : 'process-stages-ew'}>
        {stages.map((stage, i) => (
          <Stage
            key={stage.id}
            stage={stage}
            idx={i}
            isLast={i === stages.length - 1}
            openIds={openIds}
            toggle={toggle}
          />
        ))}
      </div>

      <IfEnglandWales>
      {/* Consent when you report — E&W agencies (CPS, SARC etc.) */}
      <section
        className="mt-12 tf-card p-5 sm:p-6"
        data-testid="process-consent-during-report"
        style={{
          background: 'linear-gradient(180deg, #FFFFFF 0%, #F8F4FB 100%)',
          borderColor: 'var(--tf-purple-light)',
        }}
      >
        <p className="eyebrow">CONSENT DURING YOUR REPORT</p>
        <h2
          className="font-heading mt-2"
          style={{ fontSize: 22, color: 'var(--tf-text)', letterSpacing: '-0.01em' }}
        >
          You keep the right to say yes — or no — at every step.
        </h2>
        <p
          className="font-body mt-3"
          style={{ fontSize: 13.5, color: 'var(--tf-text-secondary)', lineHeight: 1.7 }}
        >
          Reporting does not mean handing over control. At each stage below you can say
          yes, no, or &ldquo;yes to some of it&rdquo;. Ask for time. Ask for an ISVA. Ask
          &ldquo;why do you need this?&rdquo;.
        </p>
        <ul className="mt-4 flex flex-col gap-2.5" style={{ listStyle: 'none', padding: 0 }}>
          <li className="tf-card p-3.5">
            <p className="font-body font-bold" style={{ fontSize: 13, color: 'var(--tf-text)' }}>
              A forensic medical exam
            </p>
            <p className="font-body mt-1" style={{ fontSize: 12.5, color: 'var(--tf-text-secondary)', lineHeight: 1.6 }}>
              You can say yes, no, or &ldquo;yes to some parts, no to others&rdquo;. SARC
              nurses are trained to stop the moment you say so.
            </p>
          </li>
          <li className="tf-card p-3.5">
            <p className="font-body font-bold" style={{ fontSize: 13, color: 'var(--tf-text)' }}>
              An ISVA
            </p>
            <p className="font-body mt-1" style={{ fontSize: 12.5, color: 'var(--tf-text-secondary)', lineHeight: 1.6 }}>
              You choose whether to accept independent sexual-violence advocacy. It is free
              and confidential.
            </p>
          </li>
          <li className="tf-card p-3.5">
            <p className="font-body font-bold" style={{ fontSize: 13, color: 'var(--tf-text)' }}>
              Third-party materials
            </p>
            <p className="font-body mt-1" style={{ fontSize: 12.5, color: 'var(--tf-text-secondary)', lineHeight: 1.6 }}>
              School records, medical notes, therapy notes and phone data may be requested.
              You can refuse and ask why any specific item is needed.
            </p>
          </li>
          <li className="tf-card p-3.5">
            <p className="font-body font-bold" style={{ fontSize: 13, color: 'var(--tf-text)' }}>
              A Victim Personal Statement
            </p>
            <p className="font-body mt-1" style={{ fontSize: 12.5, color: 'var(--tf-text-secondary)', lineHeight: 1.6 }}>
              You choose whether to write one and whether it is read out in court.
            </p>
          </li>
          <li className="tf-card p-3.5">
            <p className="font-body font-bold" style={{ fontSize: 13, color: 'var(--tf-text)' }}>
              Special measures in court
            </p>
            <p className="font-body mt-1" style={{ fontSize: 12.5, color: 'var(--tf-text-secondary)', lineHeight: 1.6 }}>
              Screens, live video-link, a supporter, or pre-recorded evidence. You can ask
              for any of these — your ISVA will help.
            </p>
          </li>
          <li className="tf-card p-3.5">
            <p className="font-body font-bold" style={{ fontSize: 13, color: 'var(--tf-text)' }}>
              Withdrawing from the process
            </p>
            <p className="font-body mt-1" style={{ fontSize: 12.5, color: 'var(--tf-text-secondary)', lineHeight: 1.6 }}>
              You can pause or withdraw a report. In some situations the police may proceed
              without you — your wishes should still be recorded and taken into account.
            </p>
          </li>
        </ul>
        <Link
          to="/app/consent"
          data-testid="process-to-consent"
          className="inline-flex items-center gap-1.5 mt-5"
          style={{ color: 'var(--tf-purple-dark)', fontWeight: 700, fontSize: 13, textDecoration: 'none' }}
        >
          Read the full consent guide →
        </Link>
      </section>

      </IfEnglandWales>

      <IfScotland>
      {/* Consent when you report — Scotland specific (Police Scotland, COPFS, forensic exam) */}
      <section
        className="mt-12 tf-card p-5 sm:p-6"
        data-testid="process-consent-during-report-scotland"
        style={{
          background: 'linear-gradient(180deg, #FFFFFF 0%, #F8F4FB 100%)',
          borderColor: 'var(--tf-purple-light)',
        }}
      >
        <p className="eyebrow">CONSENT DURING YOUR REPORT</p>
        <h2
          className="font-heading mt-2"
          style={{ fontSize: 22, color: 'var(--tf-text)', letterSpacing: '-0.01em' }}
        >
          You keep the right to say yes — or no — at every step.
        </h2>
        <p
          className="font-body mt-3"
          style={{ fontSize: 13.5, color: 'var(--tf-text-secondary)', lineHeight: 1.7 }}
        >
          Reporting to Police Scotland or asking for a forensic medical exam is not one
          long yes — each stage is a separate choice, and you can say no or take a break
          at any moment.
        </p>
        <ul className="mt-4 flex flex-col gap-3" style={{ listStyle: 'none', padding: 0 }}>
          <li className="tf-card p-4" style={{ borderColor: 'var(--tf-purple-light)' }}>
            <p className="font-body font-bold" style={{ fontSize: 13.5, color: 'var(--tf-text)' }}>
              The forensic medical exam
            </p>
            <p className="font-body mt-1.5" style={{ fontSize: 13, color: 'var(--tf-text-secondary)', lineHeight: 1.65 }}>
              You choose which parts of the exam happen. You can pause, ask for a different
              clinician, or stop entirely. Samples can be stored for you to decide about
              reporting later. Archway (Glasgow) and comparable NHS services across every
              Scottish health board.
            </p>
          </li>
          <li className="tf-card p-4" style={{ borderColor: 'var(--tf-purple-light)' }}>
            <p className="font-body font-bold" style={{ fontSize: 13.5, color: 'var(--tf-text)' }}>
              The police interview
            </p>
            <p className="font-body mt-1.5" style={{ fontSize: 13, color: 'var(--tf-text-secondary)', lineHeight: 1.65 }}>
              You can ask for breaks, ask for questions to be repeated, ask for a specific
              officer, or ask for your Rape Crisis Scotland advocacy worker to be present.
              You can stop at any time.
            </p>
          </li>
          <li className="tf-card p-4" style={{ borderColor: 'var(--tf-purple-light)' }}>
            <p className="font-body font-bold" style={{ fontSize: 13.5, color: 'var(--tf-text)' }}>
              Withdrawing or pausing
            </p>
            <p className="font-body mt-1.5" style={{ fontSize: 13, color: 'var(--tf-text-secondary)', lineHeight: 1.65 }}>
              You can pause or withdraw a report to Police Scotland. In some situations
              COPFS may still proceed in the public interest — your wishes should still
              be recorded and taken into account.
            </p>
          </li>
        </ul>
        <Link
          to="/app/consent"
          data-testid="process-to-consent-scotland"
          className="inline-flex items-center gap-1.5 mt-5"
          style={{ color: 'var(--tf-purple-dark)', fontWeight: 700, fontSize: 13, textDecoration: 'none' }}
        >
          Read the full consent guide (Scots law) →
        </Link>
      </section>

      {/* Where to next — mirrors E&W closing block */}
      <section className="mt-10" data-testid="process-scotland-next">
        <p className="eyebrow">WHERE TO NEXT — IN SCOTLAND</p>
        <div className="mt-4 grid sm:grid-cols-2 gap-3">
          <Link
            to="/app/rights"
            data-testid="process-scot-cta-rights"
            className="tf-card tf-card-hover p-4 flex items-center justify-between gap-3"
            style={{ textDecoration: 'none', color: 'var(--tf-text)', borderColor: 'var(--tf-purple-light)' }}
          >
            <span className="font-body font-bold" style={{ fontSize: 14 }}>
              Your rights in Scotland
            </span>
            <span style={{ fontSize: 15, color: 'var(--tf-purple-dark)' }}>→</span>
          </Link>
          <Link
            to="/app/support?tab=sv"
            data-testid="process-scot-cta-support"
            className="tf-card tf-card-hover p-4 flex items-center justify-between gap-3"
            style={{ textDecoration: 'none', color: 'var(--tf-text)', borderColor: 'var(--tf-purple-light)' }}
          >
            <span className="font-body font-bold" style={{ fontSize: 14 }}>
              Scotland-specialist helplines
            </span>
            <span style={{ fontSize: 15, color: 'var(--tf-purple-dark)' }}>→</span>
          </Link>
          <Link
            to="/app/consent"
            data-testid="process-scot-cta-consent"
            className="tf-card tf-card-hover p-4 flex items-center justify-between gap-3"
            style={{ textDecoration: 'none', color: 'var(--tf-text)', borderColor: 'var(--tf-purple-light)' }}
          >
            <span className="font-body font-bold" style={{ fontSize: 14 }}>
              Consent in Scots law
            </span>
            <span style={{ fontSize: 15, color: 'var(--tf-purple-dark)' }}>→</span>
          </Link>
          <Link
            to="/app/crossed-the-line"
            data-testid="process-scot-cta-crossed"
            className="tf-card tf-card-hover p-4 flex items-center justify-between gap-3"
            style={{ textDecoration: 'none', color: 'var(--tf-text)', borderColor: 'var(--tf-purple-light)' }}
          >
            <span className="font-body font-bold" style={{ fontSize: 14 }}>
              Was it abuse or assault?
            </span>
            <span style={{ fontSize: 15, color: 'var(--tf-purple-dark)' }}>→</span>
          </Link>
        </div>
      </section>
      </IfScotland>

      <div
        className="mt-10 p-5 rounded-2xl"
        style={{ background: 'var(--tf-bg-secondary)', border: '1px solid var(--tf-card-border)' }}
      >
        <p
          className="font-body"
          style={{ fontSize: 12.5, color: 'var(--tf-text-secondary)', lineHeight: 1.6 }}
        >
          {nation === 'scotland'
            ? 'Information adapted from Rape Crisis Scotland, COPFS, Police Scotland, mygov.scot, the Victims and Witnesses (Scotland) Act 2014 and the Victims, Witnesses, and Justice Reform (Scotland) Act 2025. This is not legal advice — please speak to a Scottish solicitor or your Rape Crisis Scotland advocacy worker for advice on your own case.'
            : 'Information adapted from Rape Crisis England & Wales, the Crown Prosecution Service, the Victims\u2019 Code of Practice, and gov.uk. This is not legal advice.'}
        </p>
      </div>
    </div>
  );
}
